import { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Button } from 'basic-components';
import { CodeGenQuery } from 'dashboard-services/api/codeGen';
import Query from 'dashboard-services/api/Query';

import getApiConfig from 'api/ApiConfig';

import { MESSAGE_TYPE } from '../../constants';

import { useMutation } from '@tanstack/react-query';

import './Footer.scss';

const EMPTY_OBJECT = {};

const useGenerateCode = ({ getQueryBuilder }) =>
  useMutation({
    ...CodeGenQuery.QUERIES.generateCode(getQueryBuilder)
  })

const useExecuteCode = ({ content, requestVersionRef, isFetching, setIsFetching, setMessages, taskParams }) => {
  const dispatch = useDispatch(),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withOnError(({ message }) => {
              const errorMessage = message || "There was an error while generating code."
              setMessages(previous => previous.concat({
                message: errorMessage,
                type: MESSAGE_TYPE.BOT
              }))
            })
        , [dispatch, setMessages]),
        { mutateAsync: generateCodeCall } = useGenerateCode({ getQueryBuilder }),
        executeCall = useCallback(userPrompt => {
          if(isFetching) {
            return Promise.resolve();
          }
          const executeVersion = requestVersionRef.current
          setIsFetching(true)
          return generateCodeCall({
              inputCode: content,
              taskParams,
              userPrompt
            })
            .then(response => {
              const generatedCode = response["generated code"],
                    message = response["llm_reasoning"] || "The code has been updated based on your request."
              if(executeVersion !== requestVersionRef.current) {
                return;
              }
              setMessages(previous => previous.concat({
                generatedCode,
                message,
                sourceCode: content,
                type: MESSAGE_TYPE.BOT
              }))
            })
            .finally(() => setIsFetching(false))
        }, [content, generateCodeCall, isFetching, requestVersionRef, setIsFetching, setMessages, taskParams])
  return executeCall;
}

const Footer = ({ content, isFetching, message, requestVersion = 0, setIsFetching, setMessage, setMessages, taskParams = EMPTY_OBJECT }) => {
  const inputRef = useRef(),
        requestVersionRef = useRef(requestVersion),
        setInputHeight = useCallback(() => {
          inputRef.current.style.height = "56px";
          inputRef.current.style.height = inputRef.current.scrollHeight + 10 + "px";
        }, [])

  useEffect(() => {
    requestVersionRef.current = requestVersion
  }, [requestVersion])

  const executeCall = useExecuteCode({ content, requestVersionRef, isFetching, setIsFetching, setMessages, taskParams }),
        sendMessage = useCallback(e => {
          e?.preventDefault()
          const trimmedMessage = String(message).trim()
          if(!trimmedMessage || isFetching) {
            return;
          }
          setMessages(previous => previous.concat({
            message: trimmedMessage,
            type: MESSAGE_TYPE.USER
          }))
          executeCall(trimmedMessage)
          setMessage("")
          Promise.resolve().then(() => setInputHeight())
        }, [executeCall, isFetching, message, setInputHeight, setMessage, setMessages])

  return (
    <form
        className="ng-python-editor__chat__content__footer"
        onSubmit={sendMessage}
    >
      <textarea
          autoFocus
          className="ng-python-editor__chat__content__footer__input"
          disabled={isFetching}
          onChange={e => {
            setMessage(e.target.value)
            setInputHeight()
          }}
          onKeyDown={handleEnterSubmit}
          placeholder="Describe code changes"
          ref={inputRef}
          value={message}
      />
      <Button
          disabled={!String(message).trim() || isFetching}
          size="small"
          type="primary"
      >
        Send
      </Button>
    </form>
  )
}

Footer.propTypes = {
  content: PropTypes.string,
  isFetching: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  requestVersion: PropTypes.number,
  setIsFetching: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  setMessages: PropTypes.func.isRequired,
  taskParams: PropTypes.object
}

export default memo(Footer);

const handleEnterSubmit = e => {
  if (e.which === 13 && !e.shiftKey) {
    if (!e.repeat) {
      const newEvent = new Event("submit", {
        cancelable: true,
        bubbles: true
      });
      e.target.form.dispatchEvent(newEvent);
    }
    e.preventDefault();
  }
}
